import {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Input,
  iso2ToIso3,
  iso3ToIso2,
  PhoneInput,
  ToggleBox,
} from '@unified-checkout/design-system';
import { Envelope } from '@unified-checkout/design-system/icons/index';

import type {
  IOneCustomerDetailsProps,
  IOneCustomerRef,
} from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import type { TCountry } from '@app/dashboard/types/outerLayout/common';
import { phoneWithoutCountryCode } from '@app/dashboard/utils/phoneWithoutCountryCode';
import {
  ONE_CUSTOMER_FIELD_INITIAL_VALUES,
  SINGLE_PAYMENTS_FIELDS,
  ONE_CUSTOMER_FIELDS_PLACEHOLDERS,
} from '@app/dashboard/constants/payment-links/single-payments';
import {
  SINGLE_PAYMENT_LINKS_EVENTS,
  SINGLE_PAYMENT_LINKS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/payment_links/single';
import { validateField } from '@app/dashboard/utils/formValidations';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';

import styles from './one-customer.module.scss';

const OneCustomer = forwardRef<IOneCustomerRef, IOneCustomerDetailsProps>(
  ({ fields, setFields }, ref) => {
    const { t } = useTranslation();
    const [shouldFillData, setShouldFillData] = useState(true);
    const [country, setCountry] = useState<string>(
      () => getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY) || COUNTRIES.EGY
    );
    const [isTouched, setIsTouched] = useState(false);

    const customerDetailsRef = useRef<Record<string, string>>({
      email: '',
      phone: '',
      fullName: '',
    });

    const initialValues = useMemo(
      () => ONE_CUSTOMER_FIELD_INITIAL_VALUES(country as TCountry),
      [country]
    );

    const handleToggleChange = (checked: boolean) => {
      if (!isTouched) return;
      amplitude.track(
        SINGLE_PAYMENT_LINKS_EVENTS.CUSTOMER_DETAILS_TOGGLE_CHANGED,
        {
          [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.ALLOW_CUSTOMER_INPUT]: !checked,
        }
      );

      setShouldFillData(checked);
    };

    const handleFieldChange = ({
      value,
      fieldName,
      changedCountry,
    }: {
      value: string;
      fieldName: string;
      changedCountry?: string;
    }) => {
      const isPhoneField = fieldName === SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER;

      if (
        isPhoneField &&
        changedCountry &&
        iso2ToIso3(changedCountry) !== country
      ) {
        setCountry(iso2ToIso3(changedCountry));
        setFields((prev) => ({
          ...prev,
          [SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER]:
            ONE_CUSTOMER_FIELD_INITIAL_VALUES(iso2ToIso3(changedCountry))[
              SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER
            ],
        }));
      } else {
        const formattedValue = isPhoneField
          ? phoneWithoutCountryCode(value, country)
          : value;

        setFields((prevState) => {
          return {
            ...prevState,
            [fieldName]: {
              ...prevState[fieldName],
              value: value,
            },
          };
        });

        validateField(fields, setFields, fieldName, formattedValue);
      }
    };

    const removeDataFields = () => {
      const removableFields = [
        ...Object.keys(initialValues),
        SINGLE_PAYMENTS_FIELDS.COLLECT_EMAIL_ADDRESS,
        SINGLE_PAYMENTS_FIELDS.COLLECT_PHONE_NUMBER,
        SINGLE_PAYMENTS_FIELDS.COLLECT_CUSTOMER_NAME,
        SINGLE_PAYMENTS_FIELDS.COLLECT_BILLING_DETAILS,
      ];

      setFields((prev) => {
        const updatedFields = { ...prev };
        removableFields.forEach((field) => {
          delete updatedFields[field];
        });
        return updatedFields;
      });
    };

    const handleFocusInputs = (inputName: string, inputValue: string) => {
      customerDetailsRef.current[inputName] = inputValue;
    };

    const handleBlurInputs = (inputName: string, inputValue: string) => {
      const storedValue = customerDetailsRef.current[inputName];

      if (inputValue && storedValue !== inputValue) {
        const hasEmail = !!fields[SINGLE_PAYMENTS_FIELDS.EMAIL]?.value;
        const hasPhone = !!fields[SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER]?.value;
        const hasFullName = !!fields[SINGLE_PAYMENTS_FIELDS.FULL_NAME]?.value;

        amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.CUSTOMER_ADDED, {
          [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.HAS_EMAIL]: hasEmail,
          [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.HAS_PHONE]: hasPhone,
          [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.HAS_FULL_NAME]: hasFullName,
        });
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        shouldFillData,
      }),
      [shouldFillData]
    );

    useEffect(() => {
      if (!shouldFillData) {
        setFields((prev) => ({
          ...prev,
          ...initialValues,
        }));
      } else {
        removeDataFields();
      }

      return () => {
        removeDataFields();
      };
    }, [shouldFillData]);

    return (
      <div className={styles['one-customer']}>
        <ToggleBox
          size="sm"
          type="transparent box"
          text={t(
            'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.allowCustomerToFillData'
          )}
          checked={shouldFillData}
          onToggleChangeHandler={handleToggleChange}
          onBeforeToggleConfirmation={async () => {
            if (!isTouched) {
              setIsTouched(true);
            }
            return true;
          }}
        />
        {!shouldFillData && fields[SINGLE_PAYMENTS_FIELDS.FULL_NAME] && (
          <div className={styles['one-customer__fields']}>
            <Input
              name={SINGLE_PAYMENTS_FIELDS.FULL_NAME}
              label={t(
                'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.labels.fullName'
              )}
              labelText={`(${t('outerLayout.form.labels.optional')})`}
              placeholder={
                ONE_CUSTOMER_FIELDS_PLACEHOLDERS[
                  SINGLE_PAYMENTS_FIELDS.FULL_NAME
                ]
              }
              value={fields[SINGLE_PAYMENTS_FIELDS.FULL_NAME].value}
              error={fields[SINGLE_PAYMENTS_FIELDS.FULL_NAME].errorMsg}
              status={
                !fields[SINGLE_PAYMENTS_FIELDS.FULL_NAME].errorMsg
                  ? 'default'
                  : 'error'
              }
              onChange={(e) =>
                handleFieldChange({
                  value: e.target.value,
                  fieldName: SINGLE_PAYMENTS_FIELDS.FULL_NAME,
                })
              }
              onFocus={(e) =>
                handleFocusInputs(
                  SINGLE_PAYMENTS_FIELDS.FULL_NAME,
                  e.target.value
                )
              }
              onBlur={(e) =>
                handleBlurInputs(
                  SINGLE_PAYMENTS_FIELDS.FULL_NAME,
                  e.target.value
                )
              }
            />
            <div className={styles['one-customer__fields-row-wrapper']}>
              <div className={styles['one-customer__fields-row']}>
                <p className={styles['one-customer__fields-row-title']}>
                  {t(
                    'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.title'
                  )}
                </p>
                <div className={styles['one-customer__fields-row-inputs']}>
                  <Input
                    type="email"
                    name={SINGLE_PAYMENTS_FIELDS.EMAIL}
                    placeholder={
                      ONE_CUSTOMER_FIELDS_PLACEHOLDERS[
                        SINGLE_PAYMENTS_FIELDS.EMAIL
                      ]
                    }
                    value={fields[SINGLE_PAYMENTS_FIELDS.EMAIL].value}
                    error={fields[SINGLE_PAYMENTS_FIELDS.EMAIL].errorMsg}
                    status={
                      !fields[SINGLE_PAYMENTS_FIELDS.EMAIL].errorMsg
                        ? 'default'
                        : 'error'
                    }
                    leadingIcon={Envelope}
                    onChange={(e) =>
                      handleFieldChange({
                        value: e.target.value,
                        fieldName: SINGLE_PAYMENTS_FIELDS.EMAIL,
                      })
                    }
                    onFocus={(e) =>
                      handleFocusInputs(
                        SINGLE_PAYMENTS_FIELDS.EMAIL,
                        e.target.value
                      )
                    }
                    onBlur={(e) =>
                      handleBlurInputs(
                        SINGLE_PAYMENTS_FIELDS.EMAIL,
                        e.target.value
                      )
                    }
                  />
                  <PhoneInput
                    name={SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER}
                    placeholder={t(
                      'outerLayout.form.register.placeholders.enterUrPhoneNum'
                    )}
                    phoneNumber={phoneWithoutCountryCode(
                      fields[SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER]?.value ?? '',
                      country
                    )}
                    onChangeHandler={(value, changedCountry) =>
                      handleFieldChange({
                        value,
                        fieldName: SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER,
                        changedCountry,
                      })
                    }
                    error={fields[SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER].errorMsg}
                    country={iso3ToIso2(country)}
                    onFocus={(e) =>
                      handleFocusInputs(
                        SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER,
                        e.target.value
                      )
                    }
                    onBlur={(e) =>
                      handleBlurInputs(
                        SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER,
                        e.target.value
                      )
                    }
                  />
                </div>
              </div>
              <p className={styles['one-customer__fields-row-note']}>
                {t(
                  'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.emailOrPhoneRequired'
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default OneCustomer;
